import { Button } from '@/components/Button';
import SettingsIcon from '@/components/CardWrapper/assets/icon_setting_linear.svg?react';
import Dropdown from '@/components/Dropdown';
import ModalHeader from '@/components/Modal/ModalHeader';
import TradeLeverageForm from '@/components/TradeLeverageForm';
import { TRADE_OVERLAY_TYPE, TRADE_TYPE } from '@/constants/trade';
import { resetFormByChainId, setTradeFormType } from '@/features/trade/actions';
import { useTradeStore } from '@/features/trade/store';
import { useAppDispatch } from '@/hooks';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import EarnForm from '@/pages/earn/EarnCard/EarnForm';
import TradeAdjust from '@/pages/trade/TradeCard/TradeForm/TradeAdjust';
import TradeClose from '@/pages/trade/TradeCard/TradeForm/TradeClose';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQueryDevice } from '../MediaQuery';
import SettingsContent from './SettingsContent';
import TradeAreaOverlayModal, { shouldRenderTradeAreaOverlayModal } from './TradeAreaOverlayModal';
import './index.less';

interface TradeAreaOverlayProps {
  children?: React.ReactNode;
}

const TradeAreaOverlay: React.FC<TradeAreaOverlayProps> = ({}) => {
  const { showTradeAreaOverlay, setShowTradeAreaOverlay } = useTradeStore();
  const [isSettingsView, setIsSettingsView] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const { deviceType, isMobile } = useMediaQueryDevice();

  const isShowSettingBtn = useMemo(() => {
    if (!showTradeAreaOverlay) {
      return false;
    }
    const showSetttingTypes = [
      TRADE_OVERLAY_TYPE.CLOSE_POSITION,
      TRADE_OVERLAY_TYPE.ADD_LIQ,
      TRADE_OVERLAY_TYPE.REMOVE_LIQ,
    ];
    return showSetttingTypes.includes(showTradeAreaOverlay);
  }, [showTradeAreaOverlay]);

  const handleClose = useCallback(() => {
    if (isSettingsView) {
      setIsSettingsView(false);
    } else {
      setShowTradeAreaOverlay(undefined);
      chainId &&
        dispatch(
          resetFormByChainId({
            chainId,
          }),
        );
    }
  }, [chainId, dispatch, isSettingsView, setShowTradeAreaOverlay]);

  const handleSettingsClick = () => {
    setIsSettingsView(true);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    // Close overlay when clicking on the background
    if (e.target === e.currentTarget) {
      // handleClose();
    }
  };

  useEffect(() => {
    return () => {
      setShowTradeAreaOverlay(undefined);
    };
  }, []);

  useEffect(() => {
    if (showTradeAreaOverlay === undefined) {
      if (chainId) {
        dispatch(resetFormByChainId({ chainId }));
        dispatch(setTradeFormType({ chainId, tradeType: TRADE_TYPE.MARKET }));
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      if (showTradeAreaOverlay) {
        if (userAddr && chainId) {
          setShowTradeAreaOverlay(undefined);
          dispatch(resetFormByChainId({ chainId }));
        }
      }
    };
  }, [userAddr]);

  const overlayTitle = useMemo(() => {
    if (isSettingsView || showTradeAreaOverlay === TRADE_OVERLAY_TYPE.SETTING) {
      return 'Settings';
    }
    if (showTradeAreaOverlay === TRADE_OVERLAY_TYPE.LEVERAGE) {
      return t('common.tradePage.adjustLeverage');
    }
    if (showTradeAreaOverlay === TRADE_OVERLAY_TYPE.CLOSE_POSITION) {
      return t('common.closeP');
    }
    if (showTradeAreaOverlay === TRADE_OVERLAY_TYPE.ADJUST_MARGIN) {
      return t('common.adjustM');
    }
    if (showTradeAreaOverlay === TRADE_OVERLAY_TYPE.ADD_LIQ) {
      return t('common.earn.addLiq');
    }
    if (showTradeAreaOverlay === TRADE_OVERLAY_TYPE.REMOVE_LIQ) {
      return t('common.earn.removeLiq');
    }
    return '';
  }, [isSettingsView, showTradeAreaOverlay, t]);

  const header = useMemo(() => {
    const isPopupSettingsType =
      !isMobile &&
      (showTradeAreaOverlay === TRADE_OVERLAY_TYPE.CLOSE_POSITION ||
        showTradeAreaOverlay === TRADE_OVERLAY_TYPE.ADJUST_MARGIN ||
        showTradeAreaOverlay === TRADE_OVERLAY_TYPE.REMOVE_LIQ);

    const settingButton = (
      <Button
        className="trade-area-overlay-setting"
        type="text"
        icon={<SettingsIcon className="syn-icon" />}
        onClick={isPopupSettingsType ? undefined : handleSettingsClick}
        size="small"
      />
    );

    return (
      <ModalHeader
        title={overlayTitle}
        onClose={handleClose}
        rightContent={
          isShowSettingBtn &&
          !isSettingsView &&
          (isPopupSettingsType ? (
            <Dropdown
              open={isDropdownOpen}
              onOpenChange={setIsDropdownOpen}
              trigger={['click']}
              placement="bottomRight"
              withPadding={false}
              dropdownRender={() => (
                <div
                  style={{
                    backgroundColor: 'var(--solid-white-card)',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid var(--border-white, #FFF)',
                    boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.08)',
                    width: '360px',
                  }}>
                  <SettingsContent onSave={() => setIsDropdownOpen(false)} />
                </div>
              )}>
              {settingButton}
            </Dropdown>
          ) : (
            settingButton
          ))
        }
      />
    );
  }, [overlayTitle, handleClose, isShowSettingBtn, isSettingsView, isMobile, showTradeAreaOverlay, isDropdownOpen]);

  const content = useMemo(
    () => (
      <div className={`trade-area-overlay-content`}>
        {isSettingsView ? (
          <SettingsContent
            onSave={() => {
              setIsSettingsView(false);
            }}
          />
        ) : (
          <>
            {showTradeAreaOverlay === TRADE_OVERLAY_TYPE.LEVERAGE && <TradeLeverageForm />}
            {showTradeAreaOverlay === TRADE_OVERLAY_TYPE.CLOSE_POSITION && <TradeClose onClose={handleClose} />}
            {showTradeAreaOverlay === TRADE_OVERLAY_TYPE.ADJUST_MARGIN && <TradeAdjust onClose={handleClose} />}
            {showTradeAreaOverlay === TRADE_OVERLAY_TYPE.ADD_LIQ && <EarnForm onClose={handleClose} />}
            {showTradeAreaOverlay === TRADE_OVERLAY_TYPE.REMOVE_LIQ && (
              <EarnForm onClose={handleClose} isRemove={true} />
            )}
            {showTradeAreaOverlay === TRADE_OVERLAY_TYPE.SETTING && <SettingsContent onSave={handleClose} />}
          </>
        )}
      </div>
    ),
    [isSettingsView, showTradeAreaOverlay, handleClose],
  );

  // Only render the overlay when showTradeAreaOverlay has a value
  if (!showTradeAreaOverlay) {
    return null;
  }

  if (shouldRenderTradeAreaOverlayModal(showTradeAreaOverlay, isMobile)) {
    return <TradeAreaOverlayModal cleanupOnUnmount={false} />;
  }

  return (
    <div className={classNames(`trade-area-overlay slide-in`, deviceType)} onClick={handleOverlayClick}>
      {header}
      {content}
    </div>
  );
};

export default TradeAreaOverlay;
